import { useMemo } from "react";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons/faPencilAlt";
import { getRedirectUrlWithoutHostname } from "@iddataweb/idw-common-ui/utils";
import { LayoutTypes } from "@enums/layout-types";
import { ModalTypes } from "@enums/modal-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LandingPageTypes } from "@enums/landing-page-types";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { ClickableIcon } from "@containers/common/clickable-icon/clickable-icon";
import { useModalContext } from "@context/use-modal-context";
import { ModifyLandingPageNameModal } from "@containers/modals/modify-landing-page-name-modal/modify-landing-page-name-modal";
import styles from "./customer-redirect-url-container.less";
import { RedirectUrlOriginTypes } from "@enums/redirect-url-origin-types";
import { RedirectUrlOriginBadge } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-landing-page-tab-container/redirect-url-origin-badge/redirect-url-origin-badge";

type CustomerRedirectUrlContainerProps = {
    index: number;
    showOriginBadge: boolean;
    redirectUrl: string;
    redirectUrlOrigin: RedirectUrlOriginTypes;
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    handleRemoveRedirect: (index: number, redirectUrlOrigin: RedirectUrlOriginTypes) => void;
    handleRedirectChangeAtIndex: (newRedirectUrl: string, index: number, redirectUrl: string, redirectUrlOrigin: RedirectUrlOriginTypes) => void;
};

export const CustomerRedirectUrlContainer = (props: CustomerRedirectUrlContainerProps) => {
    const {
        index,
        showOriginBadge,
        redirectUrl,
        redirectUrlOrigin,
        layout,
        labelWidth,
        isDisabled,
        handleRemoveRedirect,
        handleRedirectChangeAtIndex
    } = props;

    const modalContext = useModalContext();

    const redirectUrlWithoutHostname = useMemo(() => {
        return getRedirectUrlWithoutHostname(redirectUrl);
    }, [redirectUrl]);

    const handleClickDeleteRedirectUrl = () => {
        handleRemoveRedirect(index, redirectUrlOrigin)
    };

    const handleClickModifyLandingPageName = () => {
        const onClickContinue = (newRedirectUrl: string) => {
            handleRedirectChangeAtIndex(newRedirectUrl, index, redirectUrl, redirectUrlOrigin);
        };

        const renderModifyLandingPageName = (onModalClose: () => void) => {
            return (
                <ModifyLandingPageNameModal
                    landingPageType={LandingPageTypes.CUSTOMER}
                    landingPageName={redirectUrl}
                    onClickContinue={onClickContinue}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.ModifyLandingPageName,
            renderFunction: renderModifyLandingPageName
        });
    };

    const afterInputElement = useMemo(() => {
        return (
            <div className={styles.buttonContainer}>
                {showOriginBadge && (
                    <RedirectUrlOriginBadge redirectUrlOrigin={redirectUrlOrigin} />
                )}
                {!isDisabled ? (<>
                    <ClickableIcon
                        controlId={`modify-customer-redirect-url-${redirectUrl}-${index}`}
                        disabled={isDisabled}
                        icon={faPencilAlt}
                        onClick={handleClickModifyLandingPageName}
                        className={styles.afterInputIcon}
                    />
                    <ClickableIcon
                        controlId={`delete-customer-redirect-url-${redirectUrl}-${index}`}
                        disabled={isDisabled}
                        icon={faTrash}
                        onClick={handleClickDeleteRedirectUrl}
                        className={styles.afterInputIcon}
                    />
                </>) : null}
            </div>
        );
    }, [
        isDisabled,
        redirectUrlOrigin,
        redirectUrlWithoutHostname,
        index,
        showOriginBadge,
        handleClickModifyLandingPageName,
        handleClickDeleteRedirectUrl
    ]);

    return (
        <InputFormGroup
            automationId={`landing-page-${redirectUrl}-redirect-url-input`}
            controlId={`landing-page-${redirectUrl}-redirect-url-input`}
            disabled={true}
            isRequired={true}
            onChange={() => { }}
            value={redirectUrlWithoutHostname}
            label={null}
            labelWidth={labelWidth}
            layout={layout}
            afterInputElement={afterInputElement}
        />
    );
};